import { ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { ChronoUnit, convert, DayOfWeek, LocalDate } from "@js-joda/core";
import classNames from "classnames";
import { useTranslation } from "next-i18next";
import { Button } from "../Button";
import { DatePicker } from "./DatePicker";

export enum PredefinedDateRangeFilterEnum {
  Today = "today",
  Yesterday = "yesterday",
  ThisWeek = "this-week",
  LastWeek = "last-week",
  LastMonth = "last-month",
  ThisMonth = "this-month",
  AllTime = "all-time",
}

const getPredefinedDateRangeFilterValue = (
  filter: PredefinedDateRangeFilterEnum
): [Date | null, Date | null] => {
  switch (filter) {
    case PredefinedDateRangeFilterEnum.Today: {
      const now = LocalDate.now();
      return [convert(now).toDate(), convert(now).toDate()];
    }
    case PredefinedDateRangeFilterEnum.Yesterday: {
      const now = LocalDate.now();
      const yesterday = now.minus(1, ChronoUnit.DAYS);
      return [convert(yesterday).toDate(), convert(yesterday).toDate()];
    }
    case PredefinedDateRangeFilterEnum.ThisWeek: {
      const now = LocalDate.now();
      const startOfWeek = now.with(DayOfWeek.MONDAY);
      const endOfWeek = startOfWeek.plus(6, ChronoUnit.DAYS);

      return [convert(startOfWeek).toDate(), convert(endOfWeek).toDate()];
    }
    case PredefinedDateRangeFilterEnum.LastWeek: {
      const now = LocalDate.now();
      const startOflastWeek = now
        .with(DayOfWeek.MONDAY)
        .minus(7, ChronoUnit.DAYS);
      const endOfLastWeek = startOflastWeek.plus(6, ChronoUnit.DAYS);

      return [
        convert(startOflastWeek).toDate(),
        convert(endOfLastWeek).toDate(),
      ];
    }
    case PredefinedDateRangeFilterEnum.ThisMonth: {
      const now = LocalDate.now();
      const startOfMonth = now.withDayOfMonth(1);
      const endOfMonth = startOfMonth.withDayOfMonth(now.lengthOfMonth());

      return [convert(startOfMonth).toDate(), convert(endOfMonth).toDate()];
    }
    case PredefinedDateRangeFilterEnum.LastMonth: {
      const now = LocalDate.now();
      const startOfMonth = now.minus(1, ChronoUnit.MONTHS).withDayOfMonth(1);
      const endOfMonth = now.withDayOfMonth(1).minusDays(1);

      return [convert(startOfMonth).toDate(), convert(endOfMonth).toDate()];
    }
    case PredefinedDateRangeFilterEnum.AllTime:
    default:
      return [null, null];
  }
};

const createCalendarContainer =
  (
    onSidebarRangeSelect: (
      startDate: Date | null,
      endDate: Date | null
    ) => void,
    onDateFilterSelect: (filter: PredefinedDateRangeFilterEnum) => void,
    onCancelClick: () => void,
    onApplyClick: () => void,
    selectedDateFilter?: PredefinedDateRangeFilterEnum
  ) =>
  ({ className, children }: { className: string; children: ReactNode }) => {
    const { t } = useTranslation(["common-components"]);

    // this should be outside of the components, but we are not setup with i18next and translation type yet for common components.
    const predefinedDateRangeFilters: {
      id: PredefinedDateRangeFilterEnum;
      name: string;
    }[] = [
      {
        id: PredefinedDateRangeFilterEnum.Today,
        name: t("common-components:today"),
      },
      {
        id: PredefinedDateRangeFilterEnum.Yesterday,
        name: t("common-components:yesterday"),
      },
      {
        id: PredefinedDateRangeFilterEnum.ThisWeek,
        name: t("common-components:this_week"),
      },
      {
        id: PredefinedDateRangeFilterEnum.LastWeek,
        name: t("common-components:last_week"),
      },
      {
        id: PredefinedDateRangeFilterEnum.ThisMonth,
        name: t("common-components:this_month"),
      },
      {
        id: PredefinedDateRangeFilterEnum.LastMonth,
        name: t("common-components:last_month"),
      },
      {
        id: PredefinedDateRangeFilterEnum.AllTime,
        name: t("common-components:all_time"),
      },
    ];

    return (
      <div className={className}>
        <div className="flex">
          <div className="font-sans flex flex-col text-sm text-secondary  gap-2 w-[192px] p-4">
            {predefinedDateRangeFilters.map((filter) => {
              return (
                <Button
                  fill="link"
                  buttonStyle="borderless"
                  className={classNames(
                    "hover:text-secondary-hover hover:bg-active",
                    {
                      "!bg-active": selectedDateFilter === filter.id,
                    }
                  )}
                  onClick={() => {
                    onSidebarRangeSelect(
                      ...getPredefinedDateRangeFilterValue(filter.id)
                    );
                    onDateFilterSelect(filter.id);
                  }}
                >
                  {filter.name}
                </Button>
              );
            })}
          </div>
          <div className="flex flex-col">
            <div key={selectedDateFilter} className="flex flex-row">
              {children}
            </div>
            <div className="flex p-4 gap-2 justify-end font-sans mt-auto">
              <Button
                fill="outline"
                buttonStyle="secondary"
                spacing="tight-hug"
                onClick={onCancelClick}
              >
                {t("common-components:cancel")}
              </Button>
              <Button spacing="tight-hug" onClick={onApplyClick}>
                {t("common-components:apply")}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  };

type DateRangePickerProps = {
  onChange: (dates: [Date | null, Date | null]) => void;
  startDate?: Date;
  endDate?: Date;
  predefinedDateRangeFilter?: PredefinedDateRangeFilterEnum;
  onPredefinedDateRangeFilterSelect?: (
    startDate: Date | null,
    endDate: Date | null,
    filter?: PredefinedDateRangeFilterEnum
  ) => void;
  tabIndex?: number;
};

export const DateRangePicker = ({
  onChange,
  startDate,
  endDate,
  predefinedDateRangeFilter,
  onPredefinedDateRangeFilterSelect,
  tabIndex,
}: DateRangePickerProps) => {
  const { t } = useTranslation(["common-components"]);
  const [selectedDateFilter, setSelectedDateFilter] = useState<
    PredefinedDateRangeFilterEnum | undefined
  >(predefinedDateRangeFilter);
  const [selectedTempDateFilter, setSelectedTempDateFilter] = useState<
    PredefinedDateRangeFilterEnum | undefined
  >(predefinedDateRangeFilter);

  // react-datepicker is using Date | null to manage the selected range so let's make it consistent
  // however the props we are exposing can still be undefined
  // so other components can use them as optional prop instead of defining null manually
  const [selectedStartDate, setStartDate] = useState<Date | null>(
    startDate ?? null
  );
  const [selectedTempStartDate, setTempStartDate] = useState<Date | null>(
    startDate ?? null
  );
  const [selectedEndDate, setEndDate] = useState<Date | null>(endDate ?? null);
  const [selectedTempEndDate, setTempEndDate] = useState<Date | null>(
    endDate ?? null
  );

  const datePickerRef = useRef<{
    focus: () => void;
    setOpen: (open: boolean) => void;
  }>(null);
  const onCancelClick = useCallback(() => {
    setStartDate(selectedTempStartDate);
    setEndDate(selectedTempEndDate);
    setSelectedDateFilter(selectedTempDateFilter);
    datePickerRef.current?.setOpen(false);
  }, [selectedTempDateFilter, selectedTempEndDate, selectedTempStartDate]);

  const onApplyClick = useCallback(() => {
    onChange([selectedStartDate, selectedEndDate]);
    onPredefinedDateRangeFilterSelect?.(
      selectedStartDate,
      selectedEndDate,
      selectedDateFilter
    );
    setTempStartDate(selectedStartDate);
    setTempEndDate(selectedEndDate);
    setSelectedTempDateFilter(selectedDateFilter);
    datePickerRef.current?.setOpen(false);
  }, [
    onChange,
    selectedStartDate,
    selectedEndDate,
    onPredefinedDateRangeFilterSelect,
    selectedDateFilter,
  ]);

  const onSidebarRangeSelect = useCallback(
    (filterStartDate: Date | null, filterEndDate: Date | null) => {
      setStartDate(filterStartDate);
      setEndDate(filterEndDate);
    },
    []
  );

  const handleOnPredefinedDateRangeFilterSelect = useCallback(
    (filter: PredefinedDateRangeFilterEnum) => {
      setSelectedDateFilter(filter);
    },
    []
  );

  useEffect(() => {
    setStartDate(startDate ?? null);
    setEndDate(endDate ?? null);
    setTempStartDate(startDate ?? null);
    setTempEndDate(endDate ?? null);
    setSelectedDateFilter(predefinedDateRangeFilter);
    setSelectedTempDateFilter(predefinedDateRangeFilter);
  }, [endDate, startDate, predefinedDateRangeFilter]);

  return (
    <DatePicker
      onChange={(dates: [Date | null, Date | null]) => {
        setStartDate(dates[0]);
        setEndDate(dates[1]);
        setSelectedDateFilter(undefined);
      }}
      placeholderText={
        !selectedStartDate && !selectedEndDate
          ? // i8next type is not setup in common components package
            // so we need to cast it to string
            (t("common-components:all_time") as string)
          : undefined
      }
      startDate={selectedStartDate}
      endDate={selectedEndDate}
      monthsShown={2}
      selectsRange
      calendarContainer={createCalendarContainer(
        onSidebarRangeSelect,
        handleOnPredefinedDateRangeFilterSelect,
        onCancelClick,
        onApplyClick,
        selectedDateFilter
      )}
      shouldCloseOnSelect={false}
      ref={datePickerRef}
      calendarStartDay={1}
      onCalendarClose={onCancelClick}
      tabIndex={tabIndex}
    />
  );
};

export default {};
